import { NarrowedElement } from '@floating-ui/react';
import { ColorPalette, darkModeColors, generateColorPalette, generateColorScale } from '../../util';
import { createContext, MutableRefObject, useContext, useEffect, useMemo, useRef } from 'react';

interface ILocalThemeContext {
  paletteColorScale: string[];
  paletteSeedColor?: string;
  colorPalette?: ColorPalette;
  colors: { background: string };
  /** Optional overrides for specific palette color values */
  paletteOverrides?: Partial<ColorPalette>;
}

export interface LocalThemeContextProps<E extends HTMLElement>
  extends Pick<ILocalThemeContext, 'paletteSeedColor' | 'paletteOverrides'> {
  children?: React.ReactNode;
  /**
   * Optional ref to target element for theme application
   * If not provided, theme will be applied to an internal wrapper div
   */
  colors?: { background?: string };
  themeTargetRef?: MutableRefObject<NarrowedElement<E> | null> | MutableRefObject<E>;
}

const LocalThemeContext = createContext<ILocalThemeContext>({
  paletteColorScale: [],
  paletteSeedColor: darkModeColors.almostBlack,
  colors: { background: darkModeColors.almostBlack },
});

export const useLocalThemeContext = () => useContext(LocalThemeContext);

export function LocalThemeContextProvider<E extends HTMLElement>({
  colors,
  children,
  themeTargetRef,
  paletteSeedColor,
  paletteOverrides,
}: LocalThemeContextProps<E>) {
  const localThemeContainerRef = useRef<HTMLDivElement>(null);

  const runtimePaletteSeedColor = paletteSeedColor ?? darkModeColors.almostBlack;
  const runtimeColors = { background: darkModeColors.almostBlack, ...colors };

  const paletteColorScale = useMemo(() => generateColorScale(runtimePaletteSeedColor), [runtimePaletteSeedColor]);
  const colorPalette = useMemo(() => generateColorPalette(runtimePaletteSeedColor), [runtimePaletteSeedColor]);

  useEffect(() => {
    const currentElement = themeTargetRef?.current ?? localThemeContainerRef.current;
    if (!currentElement) return;
    for (let i = 1; i < paletteColorScale.length + 1; i++) {
      const colorIndex = i * 100;
      const color = paletteOverrides?.[colorIndex as keyof Partial<ColorPalette>] ?? paletteColorScale[i - 1];
      currentElement.style.setProperty(`--color-palette-${colorIndex}`, hextorgb(color).join(' '));
    }
  }, [runtimePaletteSeedColor, paletteColorScale, paletteOverrides, themeTargetRef, localThemeContainerRef]);

  useEffect(() => {
    const currentElement = themeTargetRef?.current ?? localThemeContainerRef.current;
    if (!currentElement) return;
    currentElement.style.setProperty('--color-background', hextorgb(runtimeColors.background).join(' '));
  }, [runtimeColors, themeTargetRef, localThemeContainerRef]);

  return (
    <LocalThemeContext.Provider
      value={{ paletteSeedColor: runtimePaletteSeedColor, paletteColorScale, colorPalette, colors: runtimeColors }}
    >
      {themeTargetRef ? (
        children
      ) : (
        <div className="contents" ref={localThemeContainerRef}>
          {children}
        </div>
      )}
    </LocalThemeContext.Provider>
  );
}

const hexRegex = new RegExp(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/);

// Modified Code Snippet originally by StackOverflow user rezoner: https://stackoverflow.com/a/14101452
function hextorgb(hex: string) {
  if (!hexRegex.test(hex)) {
    throw new Error('Passed string is not hex. Function: hextorgb');
  }
  return [
    // @ts-expect-error ignore type
    ('0x' + hex[1] + hex[2]) | 0,
    // @ts-expect-error ignore type
    ('0x' + hex[3] + hex[4]) | 0,
    // @ts-expect-error ignore type
    ('0x' + hex[5] + hex[6]) | 0,
  ];
}
